import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Bell,
  X,
  Check,
  CheckCheck,
  Users,
  FileText,
  Calendar,
  ShoppingBag,
  AlertCircle,
  Info,
  AlertTriangle
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const ICON_MAP = {
  team_invite: Users,
  lease_created: Calendar,
  lease_expired: AlertCircle,
  lease_expiring: AlertTriangle,
  lease_reminder: Info,
  document_added: FileText,
  service_update: ShoppingBag,
  missing_documents: FileText,
};

const SEVERITY_COLORS = {
  error: "text-red-400",
  warning: "text-amber-400",
  info: "text-blue-400",
};

export const NotificationBell = () => {
  const { token } = useAuth();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);
  const dropdownRef = useRef(null);

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/notifications/stored?limit=15`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications(res.data.notifications);
      setUnreadCount(res.data.unread_count);
    } catch (err) {
      console.error("Failed to fetch notifications:", err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  // WebSocket connection
  useEffect(() => {
    if (!token) return;
    // WebSocket endpoint is at /api/ws/notifications
    const wsUrl = API.replace(/^http/, "ws") + "/ws/notifications?token=" + token;
    // Normalize URL - the path becomes /api/ws/notifications
    const normalized = new URL(wsUrl, window.location.origin).href.replace(/^http/, "ws");
    
    let ws;
    try {
      ws = new WebSocket(normalized);
      wsRef.current = ws;

      ws.onmessage = (evt) => {
        try {
          const msg = JSON.parse(evt.data);
          if (msg.event === "notification") {
            setNotifications((prev) => [msg.data, ...prev].slice(0, 20));
            setUnreadCount((c) => c + 1);
            toast.info(msg.data.message, { duration: 5000 });
          } else if (msg.event === "unread_count") {
            setUnreadCount(msg.count);
          }
        } catch (err) {
          console.error("WebSocket message parse error:", err);
        }
      };
      ws.onclose = () => {
        // Reconnect after 5s
        setTimeout(() => {
          if (wsRef.current === ws) wsRef.current = null;
        }, 5000);
      };
    } catch (err) {
      console.error("WebSocket connection error:", err);
    }

    return () => {
      if (ws) ws.close();
    };
  }, [token]);

  // Fetch on mount
  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleMarkRead = async (notifId) => {
    try {
      await axios.post(`${API}/notifications/${notifId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) =>
        prev.map((n) => (n.notif_id === notifId ? { ...n, is_read: true } : n))
      );
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (err) {
      console.error("Failed to mark notification read:", err);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all read:", err);
    }
  };

  const formatTime = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        className="text-slate-400 hover:text-white relative"
        onClick={() => {
          setOpen((o) => !o);
          if (!open) fetchNotifications();
        }}
        data-testid="notification-bell-btn"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold px-1" data-testid="notification-badge">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-slate-900 border border-slate-800 rounded-xl shadow-2xl z-50 overflow-hidden" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
            <h3 className="text-white font-semibold text-sm">Notifications</h3>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={handleMarkAllRead}
                  className="text-xs text-primary hover:text-primary/80 flex items-center gap-1"
                  data-testid="mark-all-read-btn"
                >
                  <CheckCheck className="w-3 h-3" />
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="text-slate-500 hover:text-white">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Notification List */}
          <div className="max-h-96 overflow-y-auto">
            {loading && notifications.length === 0 ? (
              <div className="py-8 text-center">
                <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="py-8 text-center">
                <Bell className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const Icon = ICON_MAP[notif.type] || Info;
                const colorClass = SEVERITY_COLORS[notif.severity] || "text-slate-400";
                return (
                  <div
                    key={notif.notif_id}
                    className={`flex items-start gap-3 px-4 py-3 border-b border-slate-800/50 hover:bg-slate-800/30 transition-colors cursor-pointer ${
                      !notif.is_read ? "bg-primary/5" : ""
                    }`}
                    onClick={() => !notif.is_read && handleMarkRead(notif.notif_id)}
                    data-testid={`notification-item-${notif.notif_id}`}
                  >
                    <div className={`mt-0.5 ${colorClass}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm leading-snug ${notif.is_read ? "text-slate-400" : "text-white"}`}>
                        {notif.message}
                      </p>
                      <p className="text-xs text-slate-600 mt-1">{formatTime(notif.created_at)}</p>
                    </div>
                    {!notif.is_read && (
                      <div className="w-2 h-2 rounded-full bg-primary mt-2 shrink-0"></div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
